import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  List,
  Row,
  Select,
  notification,
} from "antd";
import moment from "moment-timezone";
import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import "./index.css";

import Breadcrumbs from "../../../components/Breadcrumbs";
import apiLogs from "../../../services/apiLogs";
import apiOcpp from "../../../services/apiOcpp";
import firebase from "../../../services/firebaseConfig";
import { FreireContext } from "../../../utils/freireContext";
import * as texts from "../locales";
import withPermission from "../../../contexts/PermissionContext/withPermission";

const { Option } = Select;

class ListLogs extends Component {
  static contextType = FreireContext;

  state = {
    loadingStations: false,
    logs: [],
    stations: [],
    selectedStationID: "",
    selectedDate: null,
    searchTerm: "",
    loading: false,
    isConnected: false,
  };

  enterFunction = (event) => {
    if (event.keyCode === 13) {
      const logs = this.state.logs;
      if (logs.length > 0) {
        const spacer = {
          id: `spacer-${Date.now()}`,
          text: "",
          connectionUrl: "",
          clientIP: "",
        };
        this.setState({ logs: [...logs, spacer] }, () => {
          this.goToEndScroll();
        });
      }
    }
  };

  componentDidMount() {
    document.addEventListener("keydown", this.enterFunction, false);

    // Get query parameters
    const params = new URLSearchParams(this.props.location.search);
    const stationID = params.get("stationID");
    const dateParam = params.get("date");
    const search = params.get("search") || "";

    const updates = {};

    if (stationID) {
      updates.selectedStationID = stationID;
      this.props.form.setFieldsValue({ stationId: stationID });
    }

    if (search) {
      updates.searchTerm = search;
      this.props.form.setFieldsValue({ search });
    }

    const parsedDate = this.parseDateParam(dateParam);
    if (parsedDate) {
      updates.selectedDate = parsedDate;
      this.props.form.setFieldsValue({ date: parsedDate });
    }

    this.getStations();

    if (Object.keys(updates).length > 0) {
      this.setState(updates, () => {
        if (stationID && parsedDate) {
          this.loadLogs(stationID, parsedDate, search);
        }
      });
    }
  }

  componentWillUnmount() {
    this.unsubscribeLogs();
    document.removeEventListener("keydown", this.enterFunction, false);
  }

  parseDateParam = (value) => {
    if (!value) {
      return null;
    }

    const supportedFormats = ["YYYY-MM-DD", "DD-MM-YYYY"];
    for (const format of supportedFormats) {
      const parsed = moment(value, format, true);
      if (parsed.isValid()) {
        return parsed;
      }
    }

    return null;
  };

  updateQueryParams = (entries) => {
    const params = new URLSearchParams(this.props.location.search);

    Object.entries(entries).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") {
        params.delete(key);
      } else {
        params.set(key, value);
      }
    });

    const queryString = params.toString();
    const newUrl = queryString
      ? `${this.props.location.pathname}?${queryString}`
      : this.props.location.pathname;

    this.props.history.replace(newUrl);
  };

  loadLogs = async (stationID, date, searchTerm = this.state.searchTerm) => {
    if (!stationID || !date) {
      return;
    }

    const { freire } = this.context;

    this.unsubscribeLogs();

    const baseUrl = (apiLogs.defaults.baseURL || "").replace(/\/$/, "");

    if (!baseUrl) {
      console.error("Missing OCPP logs API URL");
      return;
    }

    const token = await firebase.auth()?.currentUser?.getIdToken();

    const dateString = moment(date).format("YYYY-MM-DD");
    const startDate = moment
      .utc(dateString, "YYYY-MM-DD")
      .startOf("day")
      .toISOString();
    const endDate = moment
      .utc(dateString, "YYYY-MM-DD")
      .endOf("day")
      .toISOString();

    const url = new URL(`${baseUrl}/logs/${stationID}/stream`);
    url.searchParams.set("start_date", startDate);
    url.searchParams.set("end_date", endDate);

    if (searchTerm) {
      url.searchParams.set("search", searchTerm);
    }

    if (token) {
      url.searchParams.set("token", token);
    }

    try {
      const eventSource = new EventSource(url.toString(), {
        withCredentials: true,
      });

      this.eventSource = eventSource;
      this.setState({ loading: true, logs: [], isConnected: false });

      eventSource.onopen = () => {
        this.setState({ isConnected: true, loading: false });
      };

      eventSource.addEventListener("chunk", this.handleSseChunk);

      eventSource.onerror = (error) => {
        if (eventSource.readyState === EventSource.CLOSED) {
          this.unsubscribeLogs();
          this.setState({ isConnected: false, loading: false });
          return;
        }

        console.error("Error streaming logs:", error);
        this.setState({ isConnected: false, loading: false });
        this.unsubscribeLogs();
        notification.error({
          message: freire(texts.REALTIME_CONNECTION_UNAVAILABLE),
          description: freire(texts.MANUAL_REFRESH_REQUIRED),
        });
      };
    } catch (error) {
      console.error("Error initializing log stream:", error);
      this.setState({ loading: false, isConnected: false });
      notification.error({
        message: freire(texts.REALTIME_CONNECTION_UNAVAILABLE),
        description: freire(texts.MANUAL_REFRESH_REQUIRED),
      });
    }
  };

  handleSseChunk = (event) => {
    try {
      const payload = JSON.parse(event.data);

      if (!payload || !Array.isArray(payload.items)) {
        return;
      }

      const newLogs = payload.items.map(this.formatLogEntry).filter(Boolean);

      const scroll = this.isEndOfScroll();

      this.setState(
        (prevState) => ({
          logs: [...prevState.logs, ...newLogs],
          loading: false,
        }),
        () => {
          if (scroll) this.goToEndScroll();
        }
      );
    } catch (error) {
      console.error("Error parsing log chunk:", error);
    }
  };

  formatLogEntry = (item) => {
    if (!item) {
      return null;
    }

    const stationId =
      item.stationID || this.state.selectedStationID || "UNKNOWN";
    const timestampSource =
      item.timestamp || item.receivedAt || item.createdAt || item.updatedAt;

    let formattedTimestamp = "";
    if (timestampSource) {
      const ts = moment.utc(timestampSource);

      formattedTimestamp = ts.isValid()
        ? ts.format("DD-MM-YYYY HH:mm:ss,SSS")
        : timestampSource;
    }

    const direction = item.from === "station" ? "Received" : "Sending";
    const rawLog =
      item.raw_log !== undefined
        ? item.raw_log
        : item.rawLog !== undefined
        ? item.rawLog
        : JSON.stringify(item);

    const text = `[${formattedTimestamp}] [${stationId}]: [${stationId}] | * ${direction} -> ${rawLog}`;

    const showConnection = item.from !== "ocpp";

    return {
      id: item.id || `${Date.now()}-${Math.random()}`,
      text,
      connectionUrl: showConnection ? item.connectionUrl || "" : "",
      clientIP: showConnection ? item.clientIP || "" : "",
      showConnection,
    };
  };

  unsubscribeLogs = () => {
    if (this.eventSource) {
      this.eventSource.removeEventListener("chunk", this.handleSseChunk);
      this.eventSource.close();
      this.eventSource = null;
    }
  };

  async getStations() {
    const { hasPermission } = this.props;

    this.setState({ loadingStations: true }, async () => {
      try {
        const { data: stations } = await apiOcpp.get(`stations/select`);

        const canViewUnknownLogs = hasPermission("show-unknown-station-logs");

        const unknownStation = {
          id: -1,
          name: "Não identificado",
          stationId: "UNKNOWN",
        };

        this.setState({
          stations: [
            ...stations,
            ...(canViewUnknownLogs ? [unknownStation] : []),
          ],
        });
        return stations;
      } catch (error) {
        return [];
      } finally {
        this.setState({ loadingStations: false });
      }
    });
  }

  selectStation = (stationID) => {
    this.unsubscribeLogs();

    if (!stationID) {
      this.setState({
        selectedStationID: "",
        selectedDate: null,
        logs: [],
        searchTerm: "",
        isConnected: false,
      });
      this.props.form.resetFields(["stationId", "date", "search"]);
      this.updateQueryParams({ stationID: null, date: null, search: null });
      return;
    }

    this.setState(
      {
        selectedStationID: stationID,
        selectedDate: null,
        logs: [],
        isConnected: false,
      },
      () => {
        this.props.form.resetFields(["date"]);
        this.updateQueryParams({ stationID, date: null });
      }
    );
  };

  selectDate = (date) => {
    const { selectedStationID, searchTerm } = this.state;

    if (!date || !date.isValid()) {
      this.unsubscribeLogs();
      this.setState({ selectedDate: null, logs: [], isConnected: false });
      this.updateQueryParams({ date: null });
      return;
    }

    this.setState({ selectedDate: date }, () => {
      const formattedDate = moment(date).format("YYYY-MM-DD");
      this.updateQueryParams({ date: formattedDate });

      if (selectedStationID) {
        this.loadLogs(selectedStationID, date, searchTerm);
      }
    });
  };

  handleSearch = (value = "") => {
    const { selectedStationID, selectedDate } = this.state;
    const searchValue = value || "";

    this.setState({ searchTerm: searchValue }, () => {
      this.updateQueryParams({ search: searchValue || null });

      if (selectedStationID && selectedDate) {
        this.loadLogs(selectedStationID, selectedDate, searchValue);
      }
    });
  };

  handleSearchChange = (event) => {
    const value = event.target.value;

    if (value === "") {
      this.handleSearch("");
      return;
    }

    this.setState({ searchTerm: value });
  };

  isEndOfScroll() {
    const list = document.getElementById("myList");
    if (list.scrollHeight - list.clientHeight === list.scrollTop) {
      return true;
    }

    return false;
  }

  goToEndScroll() {
    const list = document.getElementById("myList");
    list.scrollTop = list.scrollHeight;
  }

  goFullScreen() {
    var elem = document.getElementById("myList");

    if (elem.requestFullscreen) {
      elem.requestFullscreen();
    } else if (elem.mozRequestFullScreen) {
      elem.mozRequestFullScreen();
    } else if (elem.webkitRequestFullscreen) {
      elem.webkitRequestFullscreen();
    } else if (elem.msRequestFullscreen) {
      elem.msRequestFullscreen();
    }
  }

  render() {
    const { getFieldDecorator } = this.props.form;
    const {
      logs,
      stations,
      selectedStationID,
      selectedDate,
      searchTerm,
      loading,
      loadingStations,
    } = this.state;

    const { freire } = this.context;

    return (
      <div className="containerLogs">
        <Breadcrumbs
          breadcrumbs={[
            freire(texts.ADMIN),
            freire(texts.OCPP),
            freire(texts.LOGS),
          ]}
        />
        <div className="filter">
          <Form wrapperCol={{ span: 24 }}>
            <Form.Item>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <Form.Item label={freire(texts.STATION)}>
                    {getFieldDecorator("stationId")(
                      <Select
                        showSearch
                        allowClear
                        loading={loadingStations}
                        placeholder={
                          loadingStations
                            ? freire(texts.CHARGING)
                            : freire(texts.SELECT_STATION)
                        }
                        optionFilterProp="label"
                        onChange={this.selectStation}
                        onDropdownVisibleChange={(opened) => {
                          if (opened && stations.length === 0) {
                            this.getStations();
                          }
                        }}
                      >
                        {stations.map((station) => {
                          return (
                            <Option
                              label={`${station.stationId} (${station.name})`}
                              key={station.stationId}
                              value={station.stationId}
                            >
                              {station.stationId}{" "}
                              <span style={{ fontSize: 12, color: "#878787" }}>
                                ({station.name})
                              </span>
                            </Option>
                          );
                        })}
                      </Select>
                    )}
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item label={freire(texts.DAY_REFERRING)}>
                    {getFieldDecorator("date")(
                      <DatePicker
                        style={{ width: "100%" }}
                        format="YYYY-MM-DD"
                        allowClear
                        disabled={selectedStationID === ""}
                        placeholder={freire(texts.SELECT_DATE)}
                        onChange={(value) => this.selectDate(value)}
                      />
                    )}
                  </Form.Item>
                </Col>
              </Row>
            </Form.Item>
            <Form.Item label={freire(texts.SEARCH)}>
              {getFieldDecorator("search", {
                initialValue: searchTerm,
              })(
                <Input.Search
                  allowClear
                  enterButton={freire(texts.SEARCH)}
                  placeholder={freire(texts.SEARCH)}
                  disabled={!selectedStationID || !selectedDate}
                  onSearch={this.handleSearch}
                  onChange={this.handleSearchChange}
                />
              )}
            </Form.Item>
          </Form>
        </div>

        {logs.length > 0 ? (
          <div style={{ position: "relative" }}>
            <Button
              style={{ position: "absolute", top: 0, right: 15, zIndex: 9999 }}
              icon="fullscreen"
              onClick={this.goFullScreen}
            >
              {freire(texts.FULLSCREEN)}
            </Button>
          </div>
        ) : null}

        <div id="myList" style={{ overflowY: "scroll", position: "relative" }}>
          <List
            bordered
            style={{ backgroundColor: "#FFF" }}
            size="small"
            dataSource={logs}
            rowKey={(item) => item.id}
            loading={loading}
            renderItem={(item) => (
              <List.Item className="log-item">
                <div className="log-line">{item.text}</div>
                {item.showConnection ? (
                  <div className="log-connection">
                    connectionUrl: {item.connectionUrl || "-"} | clientIP:{" "}
                    {item.clientIP || "-"}
                  </div>
                ) : null}
              </List.Item>
            )}
          />
        </div>
      </div>
    );
  }
}

const PageListLogs = Form.create({ name: "filters" })(ListLogs);

export default withPermission(withRouter(PageListLogs));
